import { spokenPostalCodes } from "./pronunciation";
import type { RadioMessage } from "../domain/model";

export const radioPriority: Record<RadioMessage["kind"], number> = {
  request: 100,
  speechRequest: 92,
  conversation: 105,
  call: 90,
  alarm: 80,
  warning: 95,
  clear: 60,
  situation: 50,
  status: 40,
};
export class RadioInbox {
  private seen = new Set<string>();
  private initialized = false;
  private sequence = 0;
  disconnect() {
    this.initialized = false;
  }
  take(messages: RadioMessage[]) {
    const latest = messages.at(-1)?.sequence ?? 0;
    if (!this.initialized || latest < this.sequence) {
      this.seen = new Set(messages.map((m) => m.id));
      this.initialized = true;
      this.sequence = latest;
      return [];
    }
    const fresh = messages.filter((m) => !this.seen.has(m.id));
    this.sequence = latest;
    for (const m of fresh) this.seen.add(m.id);
    if (this.seen.size > 1000) this.seen = new Set(messages.map((m) => m.id));
    return fresh;
  }
}
export type SpeechMessage = RadioMessage & {
  voicePreview?: boolean;
  manual?: boolean;
  sourceId?: string;
  resumeFrom?: number;
};
const priority = (m: SpeechMessage) =>
  m.manual || m.voicePreview ? 110 : radioPriority[m.kind];
const key = (m: SpeechMessage) =>
  `${m.kind}:${m.vehicleId ?? m.incidentId ?? m.id}`;
export class RadioQueue {
  private pending: { message: SpeechMessage; at: number }[] = [];
  get length() {
    return this.pending.length;
  }
  clear() {
    this.pending = [];
  }
  add(message: SpeechMessage, now = Date.now()) {
    if (this.pending.some((entry) => entry.message.id === message.id)) return;
    this.pending = this.pending.filter((entry) => {
      const old = entry.message;
      if (
        message.kind === "clear" &&
        old.incidentId === message.incidentId &&
        !old.manual &&
        ["status", "situation", "warning", "speechRequest"].includes(old.kind)
      )
        return false;
      if (old.manual || old.voicePreview || message.manual) return true;
      // A new situation supersedes an earlier situation even when another unit now reports.
      if (
        message.kind === "situation" &&
        old.kind === "situation" &&
        message.incidentId === old.incidentId
      )
        return false;
      if (
        message.kind === "conversation" &&
        old.kind === "speechRequest" &&
        old.vehicleId === message.vehicleId
      )
        return false;
      // Separate dispatcher/crew turns must not replace each other.
      if (message.kind === "conversation") return true;
      return key(old) !== key(message);
    });
    this.pending.push({ message, at: now });
    this.pending.sort(
      (a, b) =>
        priority(b.message) - priority(a.message) ||
        (a.message.kind === "conversation" && b.message.kind === "conversation"
          ? a.at - b.at
          : b.at - a.at),
    );
    this.pending = this.pending.slice(0, 4);
  }
  next(now = Date.now()) {
    this.pending = this.pending.filter(
      (entry) =>
        now - entry.at <=
        (entry.message.manual ||
        ["request", "conversation"].includes(entry.message.kind)
          ? 90000
          : 25000),
    );
    return this.pending.shift()?.message;
  }
}
/** Real wall time, independent of 1×/5×/20× simulation speed. */
export class RadioTraffic {
  private lastSituation = -Infinity;
  private lastStatus = -Infinity;
  accept(message: RadioMessage, now = Date.now()) {
    if (message.kind === "situation") {
      if (now - this.lastSituation < 20000) return false;
      this.lastSituation = now;
    }
    if (message.kind === "status") {
      if (now - this.lastStatus < 8000) return false;
      this.lastStatus = now;
    }
    return true;
  }
}

export function spokenText(text: string) {
  const words: Record<string, string> = {
    HLF: "Hilfeleistungslöschfahrzeug",
    LF: "Löschfahrzeug",
    DLK: "Drehleiter",
    RTW: "Rettungswagen",
    NEF: "Notarzteinsatzfahrzeug",
    KTW: "Krankentransportwagen",
    FuStW: "Funkstreifenwagen",
    HvO: "Helfer vor Ort",
    RTH: "Rettungshubschrauber",
    ITH: "Intensivtransporthubschrauber",
    DRK: "Deutsches Rotes Kreuz",
    ASB: "Arbeiter Samariter Bund",
    THW: "Technisches Hilfswerk",
    SEK: "Spezialeinsatzkommando",
    MEK: "Mobiles Einsatzkommando",
    ELW: "Einsatzleitwagen",
    TLF: "Tanklöschfahrzeug",
    RW: "Rüstwagen",
    DLRG: "Deutsche Lebensrettungsgesellschaft",
    ILS: "Leitstelle",
    IRLS: "Leitstelle",
    VU: "Verkehrsunfall",
    B3: "Brand drei",
  };
  return spokenPostalCodes(text)
    .replace(/\bSIM\s+/g, "")
    .replace(
      /\b(HLF|LF|DLK|RTW|NEF|KTW|FuStW|HvO|RTH|ITH|DRK|ASB|THW|SEK|MEK|ELW|TLF|RW|DLRG|ILS|IRLS|VU|B3)\b/g,
      (word) => words[word],
    )
    .replace(
      /\b(\d{1,3})\/(\d{1,3})(?:-(\d{1,3}))?\b/g,
      (_, a: string, b: string, c?: string) =>
        [a, b, c]
          .filter((n): n is string => n !== undefined)
          .map(spokenNumber)
          .join(", "),
    )
    .replace(/\bStr\./g, "Straße");
}

function spokenNumber(value: string): string {
  const small = [
    "null",
    "eins",
    "zwei",
    "drei",
    "vier",
    "fünf",
    "sechs",
    "sieben",
    "acht",
    "neun",
    "zehn",
    "elf",
    "zwölf",
    "dreizehn",
    "vierzehn",
    "fünfzehn",
    "sechzehn",
    "siebzehn",
    "achtzehn",
    "neunzehn",
  ];
  const tens = [
    "",
    "",
    "zwanzig",
    "dreißig",
    "vierzig",
    "fünfzig",
    "sechzig",
    "siebzig",
    "achtzig",
    "neunzig",
  ];
  const n = Number(value);
  if ((value.startsWith("0") && value.length > 1) || n > 99)
    return [...value].map((d) => small[Number(d)]).join(" ");
  if (n < 20) return small[n];
  return (
    (n % 10 ? (n % 10 === 1 ? "ein" : small[n % 10]) + "und" : "") +
    tens[Math.floor(n / 10)]
  );
}
